"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.OutboundCostClient = void 0;
const base_client_1 = require("@deliverr/base-client");
const commons_logs_1 = require("@deliverr/commons-logs");
const _ = require("lodash");
const OUTBOUND_COST_SERVICE_URL = process.env.OUTBOUND_COST_SERVICE_URL;
const OUTBOUND_COST_BASIC_AUTH_USER = process.env.OUTBOUND_COST_BASIC_AUTH_USER;
const OUTBOUND_COST_BASIC_AUTH_PASSWORD = process.env.OUTBOUND_COST_BASIC_AUTH_PASSWORD;
const DEFAULT_MAX_OCE = 150.0;
class OutboundCostClient extends base_client_1.ApiBasicAuthClient {
    constructor() {
        super({
            baseURL: `${OUTBOUND_COST_SERVICE_URL}/`,
            basicAuthUser: OUTBOUND_COST_BASIC_AUTH_USER,
            basicAuthPassword: OUTBOUND_COST_BASIC_AUTH_PASSWORD,
        });
        if (!OUTBOUND_COST_SERVICE_URL) {
            commons_logs_1.log.warn("OUTBOUND_COST_SERVICE_URL environment variable is not set");
        }
        if (!OUTBOUND_COST_BASIC_AUTH_USER || !OUTBOUND_COST_BASIC_AUTH_PASSWORD) {
            commons_logs_1.log.warn("OUTBOUND_COST_BASIC_AUTH_USER and OUTBOUND_COST_BASIC_AUTH_PASSWORD environment variables are not set");
        }
    }
    async hasEstimateForWarehouse(warehouseId) {
        return this.get(`v1/validate/${warehouseId}`);
    }
    async getUnweightedOutboundCostsForWarehousesAndWeights(warehouseIds, weights) {
        return this.get("v4/costs", { warehouseIds, weights });
    }
    async getFilteredOutboundCostsForWarehousesAndWeights(warehouseIds, weights) {
        const unfilteredOutboundCosts = await this.getOutboundCostsForWarehousesAndWeights(warehouseIds, weights);
        const warehouseIdsList = [];
        _.forEach(unfilteredOutboundCosts, (dayRangeCosts) => {
            warehouseIdsList.push(_.keys(dayRangeCosts));
        });
        const missingWarehouseIds = new Set(_.xor(...warehouseIdsList));
        _.forEach(unfilteredOutboundCosts, (dayRangeCosts) => {
            _.forEach(weights, (weightTier) => {
                const checkWeight = Number.isInteger(weightTier) ? weightTier + ".0" : weightTier;
                _.forEach(_.keys(dayRangeCosts), (warehouseId) => {
                    if (_.isEmpty(dayRangeCosts[warehouseId][checkWeight])) {
                        missingWarehouseIds.add(warehouseId);
                        return false;
                    }
                });
            });
        });
        missingWarehouseIds.forEach((missingWarehouseId) => {
            _.forEach(unfilteredOutboundCosts, (dayRangeCosts) => {
                delete dayRangeCosts[missingWarehouseId];
            });
        });
        return unfilteredOutboundCosts;
    }
    async getOutboundCostsForWarehousesAndWeights(warehouseIds, weights) {
        const unweightedOutboundCosts = await this.getUnweightedOutboundCostsForWarehousesAndWeights(warehouseIds, weights);
        return this.formatUnweightOutboundCosts(unweightedOutboundCosts);
    }
    weightOutboundCosts(costsByToZip) {
        return _.fromPairs(_.keys(costsByToZip).map((toZip) => [toZip, costsByToZip[toZip][0] || DEFAULT_MAX_OCE]));
    }
    async getActiveEstimateId() {
        return this.get("v1/estimate");
    }
    formatUnweightOutboundCosts(unweightedOutboundCosts) {
        const outboundCosts = {
            twoDayCosts: {},
            sevenDayCosts: {},
        };
        for (const daysKey of ["twoDayCosts", "sevenDayCosts"]) {
            for (const warehouseId of _.keys(unweightedOutboundCosts[daysKey])) {
                outboundCosts[daysKey][warehouseId] = {};
                for (const weight of _.keys(unweightedOutboundCosts[daysKey][warehouseId])) {
                    outboundCosts[daysKey][warehouseId][weight] = this.weightOutboundCosts(unweightedOutboundCosts[daysKey][warehouseId][weight]);
                }
            }
        }
        return {
            twoDayCosts: outboundCosts.twoDayCosts,
            sevenDayCosts: outboundCosts.sevenDayCosts,
        };
    }
    async getWarehouseRates(warehouseId, shippingMethods, weight, toZip5, marketplaceId, rateSheetVersion) {
        return this.get(`v1/rates/${warehouseId}/${weight}/${toZip5}`, {
            shippingMethods,
            marketplaceId,
            rateSheetVersion,
        });
    }
    async getSellerConfig(sellerId) {
        return this.get(`/v1/seller-config/${sellerId}`);
    }
    async insertSellerConfig(sellerConfigRequests) {
        return this.post(`/v1/seller-config`, sellerConfigRequests);
    }
    splitShippingMethodCostRequest(shippingMethodCostRequest) {
        return shippingMethodCostRequest.warehouseIds.reduce((acc, warehouseId) => {
            const combinationsForCurrentWarehouse = shippingMethodCostRequest.weights.map((weight) => (Object.assign(Object.assign({}, shippingMethodCostRequest), { warehouseIds: [warehouseId], weights: [weight] })));
            return acc.concat(combinationsForCurrentWarehouse);
        }, []);
    }
    mergeWarehouseCosts(a, b) {
        const output = Object.assign({}, a);
        for (const [warehouseId, warehouseData] of Object.entries(b)) {
            if (!output[warehouseId]) {
                output[warehouseId] = {};
            }
            for (const [weight, shippingMethods] of Object.entries(warehouseData)) {
                output[warehouseId][Number(weight)] = Object.assign(Object.assign({}, (output[warehouseId][Number(weight)] || {})), shippingMethods);
            }
        }
        return output;
    }
    mergeShippingMethodCostResponses(shippingMethodCostResponses) {
        return shippingMethodCostResponses.reduce((acc, curr) => {
            return {
                estimateId: curr.estimateId,
                BASE_RATE: this.mergeWarehouseCosts(acc.BASE_RATE, curr.BASE_RATE),
                WALMART: curr.WALMART ? this.mergeWarehouseCosts(acc.WALMART || {}, curr.WALMART) : acc.WALMART,
            };
        }, {
            estimateId: -1,
            BASE_RATE: {},
        });
    }
    async getShippingMethodCostsForWarehousesAndWeights(shippingMethodCostRequest) {
        const shippingMethodCostRequests = this.splitShippingMethodCostRequest(shippingMethodCostRequest);
        const responses = await Promise.all(shippingMethodCostRequests.map(async (smallShippingMethodCostRequest) => this.post(`/v1/shippingMethod/costs`, smallShippingMethodCostRequest)));
        return this.mergeShippingMethodCostResponses(responses);
    }
}
exports.OutboundCostClient = OutboundCostClient;
//# sourceMappingURL=OutboundCostClient.js.map